import React, {useCallback, useState, useEffect, useMemo} from 'react';
import { Menu, Button, Tooltip } from 'antd';
import {
  UserOutlined,
  BankOutlined,
  SettingOutlined,
  TagOutlined,
  LinkOutlined,
  TeamOutlined,
  FileTextOutlined,
  StarOutlined,
  StarFilled,
  QuestionCircleOutlined,
  StockOutlined,
  CarOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import axios from 'axios';
import '../../App.css';

const iconMap = {
  'LinkOutlined': LinkOutlined,
  'BankOutlined': BankOutlined,
  'TeamOutlined': TeamOutlined,
  'FileTextOutlined': FileTextOutlined,
  'UserOutlined': UserOutlined,
  'TagOutlined': TagOutlined,
  'QuestionCircleOutlined': QuestionCircleOutlined,
  'SettingOutlined': SettingOutlined,
  'StockOutlined': StockOutlined,
  'CarOutlined': CarOutlined,
  'GlobalOutlined': GlobalOutlined,
};

const SideMenu = ({ collapsed, onCollapseChange }) => {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [sideMenu, setSideMenu] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedCollapsed = localStorage.getItem('sideMenuCollapsed');
    if (savedCollapsed !== null) {
      return JSON.parse(savedCollapsed);
    }
    return collapsed || false;
  });
  const [favoriteMenus, setFavoriteMenus] = useState([]);

  useEffect(() => {
    if (collapsed !== undefined) {
      setIsCollapsed(collapsed);
    }
  }, [collapsed]);

  useEffect(() => {
    const loadSideMenu = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/feed/side-menu`, {
          headers: getAuthHeaders()
        });
        if (response.data.success) {
          setSideMenu(response.data.sideMenu || []);
        }
      } catch (error) {
        console.error('Side menu yüklenemedi:', error);
      }
    };
    if (user) {
      loadSideMenu();
    }
  }, [getAuthHeaders]);

  const goTo = useCallback((url_path, openInNewTab = false) => {
    if (openInNewTab) {
      window.open(url_path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url_path);
    }
  }, [navigate]);

  const goToHome = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  const getRouteForMenuKey = useCallback((menuKey) => {
    const menuItem = sideMenu.find(item => item.id === menuKey || item.id === parseInt(menuKey));
    return menuItem?.url_path || '';
  }, [sideMenu]);
  
  const hasPositionAccess = useCallback((positionAccess) => {
    if (!positionAccess || positionAccess === null || positionAccess === '') {
      return true;
    }
    
    if (!user?.pozisyon) {
      return false;
    }
    
    if (typeof positionAccess === 'string') {
      return positionAccess === user.pozisyon || positionAccess.toString() === user.pozisyon.toString();
    }
    
    if (typeof positionAccess === 'number') {
      return positionAccess === user.pozisyon || positionAccess.toString() === user.pozisyon.toString();
    }
    
    if (Array.isArray(positionAccess)) {
      return positionAccess.includes(user.pozisyon) || 
             positionAccess.some(pos => pos.toString() === user.pozisyon.toString());
    }
    
    return false;
  }, [user?.pozisyon]);

  const isFavorite = useCallback((menuKey) => {
    return favoriteMenus.includes(menuKey.toString());
  }, [favoriteMenus]);

  const toggleFavorite = useCallback(async (menuKey, e) => {
    e.stopPropagation();
    e.preventDefault();
    const route = getRouteForMenuKey(menuKey);
    const url = `${API_BASE_URL}/feed/favorites/menus/toggle?menu=${encodeURIComponent(menuKey)}&route=${encodeURIComponent(route)}`;
    try {
      const response = await axios.post(
        url,
        {},
        { 
          headers: getAuthHeaders()
        }
      );
        
      if (response.data.success) {
        setFavoriteMenus(response.data.favorites || []);
        window.dispatchEvent(new CustomEvent('favoriteMenuChanged'));
      }
    } catch (error) {
      console.error('Favori toggle hatası:', error);
    }
  }, [getRouteForMenuKey, getAuthHeaders]);

  const renderStarIcon = useCallback((menuKey) => {
    const starred = isFavorite(menuKey);
    return (
      <Tooltip title={starred ? "Favorilerden çıkar" : "Favorilere ekle"}>
        <span
          onClick={(e) => toggleFavorite(menuKey, e)}
          style={{
            marginLeft: '8px',
            cursor: 'pointer',
            color: starred ? '#faad14' : '#8c8c8c',
            fontSize: '14px'
          }}
        >
          {starred ? <StarFilled /> : <StarOutlined />}
        </span>
      </Tooltip>
    );
  }, [isFavorite, toggleFavorite]);

  const getIconComponent = useCallback((iconName) => {
    if (!iconName) return null;
    const IconComponent = iconMap[iconName];
    return IconComponent ? React.createElement(IconComponent) : null;
  }, []);

  const buildMenuItems = useCallback((menuArray) => {
    if (!Array.isArray(menuArray) || menuArray.length === 0) {
      return [];
    }

    const menuMap = new Map();
    menuArray.forEach(item => {
      menuMap.set(item.id, {
        ...item,
        children: []
      });
    });

    const sortedArray = [...menuArray].sort((a, b) => a.id - b.id);
    const parentGroups = new Map();
    sortedArray.forEach(item => {
      const parentValue = item.parent;
      if (!parentGroups.has(parentValue)) {
        parentGroups.set(parentValue, []);
      }
      parentGroups.get(parentValue).push(item);
    });
    
    parentGroups.forEach((items, parentValue) => {
      if (items.length > 0) {
        const parentItem = items[0]; 
        items.forEach((item, index) => {
          if (index > 0 && item.parent === parentValue) {
            const parentInMap = menuMap.get(parentItem.id);
            if (parentInMap) {
              parentInMap.children.push(item.id);
            }
          }
        });
      }
    });

    const childIds = new Set();
    menuMap.forEach(item => {
      item.children.forEach(childId => {
        childIds.add(childId);
      });
    });

    const rootItems = [];
    
    parentGroups.forEach((items, parentValue) => {
      if (items.length > 0 && parentValue !== 0 && parentValue !== null) {
        const firstItem = items[0]; 
        if (!childIds.has(firstItem.id)) {
          rootItems.push(firstItem.id);
        }
      }
    });

    const createMenuItem = (itemId, path = []) => {
      if (path.includes(itemId)) {
        console.warn(`Circular reference detected for menu item ${itemId}. Path:`, path);
        return null;
      }

      const item = menuMap.get(itemId);
      if (!item) return null;

      if (!hasPositionAccess(item.position_access)) {
        return null;
      }

      const newPath = [...path, itemId];

      let childrenItems = [];
      if (item.children && item.children.length > 0) {
        childrenItems = item.children
          .map(childId => {
            if (childId === itemId) {
              console.warn(`Self-reference detected: item ${itemId} has itself as child`);
              return null;
            }
            return createMenuItem(childId, newPath);
          })
          .filter(child => child !== null);
      }

      const isParent = childrenItems.length > 0;

      const menuItem = {
        key: item.id.toString(),
        icon: getIconComponent(item.icon),
        label: (
          <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{item.name}</span>
            {/* id >= 9000: backend'in koddan eklediği öğe (feed.py) — side_menu'de yok, favorilenemez */}
            {!isParent && item.id < 9000 && renderStarIcon(item.id.toString())}
          </span>
        ),
      };

      if (childrenItems.length > 0) {
        menuItem.children = childrenItems;
      }

      return menuItem;
    };
    return rootItems
      .map(itemId => createMenuItem(itemId, []))
      .filter(item => item !== null);
  }, [getIconComponent, renderStarIcon, hasPositionAccess]);

  const menuItems = useMemo(() => {
    return buildMenuItems(sideMenu);
  }, [sideMenu, buildMenuItems]);

  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/feed/favorites/menus`, {
          headers: getAuthHeaders()
        });
        if (response.data.success) {
          const menuKeys = response.data.favorites.map(fav => fav.menu_key || fav);
          setFavoriteMenus(menuKeys);
        }
      } catch (error) {
        console.error('Favori menüler yüklenemedi:', error);
      }
    };

    if (user) {
      loadFavorites();
    }
  }, [user, getAuthHeaders]);

  return (
    <div>
      <div
        className="logo"
        onClick={goToHome}
        style={{
          textAlign: 'center',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          padding: '10px',
          borderBottom: '1px solid #001529',
          position: 'relative'
        }}
      >
        <img
          src="https://dosya.atmturkiye.com/atmpoint/img/flogo.png"
          style={{
            width: isCollapsed ? '40px' : '130px',
            height: 'auto',
            transition: 'width 0.3s ease'
          }}
        />
      </div>
      <Menu
        theme="dark"
        mode="inline"
        inlineCollapsed={isCollapsed}
        onSelect={({ key, domEvent }) => {
          const menuItem = sideMenu.find(item => item.id === parseInt(key));
          if (menuItem && menuItem.url_path) {
            const openInNewTab = domEvent?.ctrlKey || domEvent?.metaKey; // ctrl + click or cmd + click sayfayı yeni sekmede açmak için
            goTo(menuItem.url_path, openInNewTab);
          }
        }}
        items={menuItems}
      />
    </div>
  );
};
const MemoizedSideMenu = React.memo(SideMenu);

export default MemoizedSideMenu;